import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, MessageCircle, Loader2, Users, Circle, Gamepad2,
  ChevronDown, ChevronUp, RefreshCw, Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LobbyShoutbox = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'players'
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lobby/shoutbox`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error('Error fetching shoutbox:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOnlinePlayers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lobby/online-players`, { withCredentials: true });
      setOnlinePlayers(res.data.players || []);
    } catch (error) {
      console.error('Error fetching online players:', error);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    fetchOnlinePlayers();
    
    // Poll every 5 seconds for new messages
    pollIntervalRef.current = setInterval(() => {
      fetchMessages();
      fetchOnlinePlayers();
    }, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchMessages, fetchOnlinePlayers]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !user) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/lobby/shoutbox/send`,
        { message: newMessage.trim() },
        { withCredentials: true }
      );
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleQuickChallenge = (playerId, playerName) => {
    navigate(`/create-match?challenge=${playerId}&name=${encodeURIComponent(playerName)}`);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isMyMessage = (senderId) => senderId === user?.user_id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-xl overflow-hidden"
      data-testid="lobby-shoutbox"
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-[#7000FF]/20 to-[#00F0FF]/20 hover:from-[#7000FF]/30 hover:to-[#00F0FF]/30 transition-colors"
        data-testid="shoutbox-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <MessageCircle className="w-5 h-5 text-[#00F0FF]" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#00FF94] rounded-full animate-pulse" />
          </div>
          <span className="font-semibold">Lobby Chat</span>
          <span className="px-2 py-0.5 bg-[#00FF94]/20 text-[#00FF94] text-xs rounded-full flex items-center gap-1">
            <Circle className="w-2 h-2 fill-current" />
            {onlinePlayers.length} online
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-white/50" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/50" />
        )}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Tabs */}
            <div className="flex border-b border-white/10">
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'chat' 
                    ? 'text-[#00F0FF] border-b-2 border-[#00F0FF]' 
                    : 'text-white/50 hover:text-white'
                }`}
              >
                <MessageCircle className="w-4 h-4 inline mr-2" />
                Chat
              </button>
              <button
                onClick={() => setActiveTab('players')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'players' 
                    ? 'text-[#00FF94] border-b-2 border-[#00FF94]' 
                    : 'text-white/50 hover:text-white'
                }`}
              >
                <Users className="w-4 h-4 inline mr-2" />
                Online ({onlinePlayers.length})
              </button>
            </div>

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <>
                <div className="h-48 overflow-y-auto p-3 space-y-2 bg-black/20" data-testid="shoutbox-messages">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-6 h-6 animate-spin text-white/50" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-white/40">
                      <MessageCircle className="w-8 h-8 mb-2" />
                      <p className="text-sm">No messages yet</p>
                      <p className="text-xs">Be the first to say hi!</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((msg, index) => (
                        <motion.div
                          key={msg.message_id || index}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex gap-2 ${isMyMessage(msg.sender_id) ? 'justify-end' : ''}`}
                        >
                          <div className={`max-w-[85%] ${isMyMessage(msg.sender_id) ? 'order-2' : ''}`}>
                            <div className="flex items-center gap-2 mb-0.5">
                              {!isMyMessage(msg.sender_id) && (
                                <span className="text-xs font-medium text-[#00F0FF]">{msg.sender_name}</span>
                              )}
                              <span className="text-xs text-white/30">{formatTime(msg.created_at)}</span>
                            </div>
                            <div className={`px-3 py-1.5 rounded-lg text-sm ${
                              isMyMessage(msg.sender_id)
                                ? 'bg-[#7000FF] text-white'
                                : 'bg-white/10 text-white/90'
                            }`}>
                              {msg.message}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Input */}
                {user ? (
                  <form onSubmit={handleSend} className="p-3 border-t border-white/10">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Looking for a match..."
                        className="flex-1 bg-white/5 border-white/10 text-sm h-9"
                        maxLength={200}
                        disabled={sending}
                        data-testid="shoutbox-input"
                      />
                      <Button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        size="sm"
                        className="bg-[#7000FF] hover:bg-[#7000FF]/80 h-9"
                        data-testid="shoutbox-send-btn"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="p-3 border-t border-white/10 text-center">
                    <p className="text-white/50 text-sm">Sign in to chat</p>
                  </div>
                )}
              </>
            )}

            {/* Players Tab */}
            {activeTab === 'players' && (
              <div className="h-48 overflow-y-auto p-3 space-y-2 bg-black/20">
                {onlinePlayers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-white/40">
                    <Users className="w-8 h-8 mb-2" />
                    <p className="text-sm">No players online</p>
                  </div>
                ) : (
                  onlinePlayers.map((player) => (
                    <motion.div
                      key={player.user_id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center justify-between p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7000FF] to-[#00F0FF] flex items-center justify-center text-sm font-bold">
                            {player.name?.charAt(0) || '?'}
                          </div>
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#00FF94] rounded-full border-2 border-[#0A0A0B]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{player.name}</p>
                          <p className="text-xs text-white/40">
                            {player.wins || 0}W - {player.losses || 0}L
                          </p>
                        </div>
                      </div>
                      {user && player.user_id !== user.user_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleQuickChallenge(player.user_id, player.name)}
                          className="h-7 text-xs border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                          data-testid={`challenge-${player.user_id}`}
                        >
                          <Zap className="w-3 h-3 mr-1" />
                          Challenge
                        </Button>
                      )}
                    </motion.div>
                  ))
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LobbyShoutbox;
